/**
 * Mein Tag – der persönliche Arbeitsvorrat (v7, 19.09.2026).
 *
 * Ein Vorgang liegt bei genau einer Person; welche, sagt die
 * Zuständigkeitsmatrix im Admin Center. Hier steht ausschließlich, was laut
 * Matrix bei mir liegt – alles andere steht unter „Vorgänge".
 *
 * Was v7 gegenüber v6 ändert:
 *
 *  - Jede Rubrik hat einen eigenen Farbton, je Person wählbar. Der Ton besteht
 *    aus drei Werten; gefüllt wird immer mit `akzTxt`, weil der helle Akzent
 *    keine weiße Schrift trägt.
 *  - Die Kacheln sind Quadrate mit der Tätigkeit oben und der Zahl unten
 *    rechts. Ist eine Rubrik gewählt, treten die anderen sieben matt zurück –
 *    das ist der Kern der Übersicht. Nur „n dringend" bleibt immer rot.
 *  - Die Liste besteht aus Karten, eine je Vorgang, mit der Kante in der Farbe
 *    ihrer Rubrik. Der Rubrikkopf ist nur noch Text.
 *  - Flächen gibt es nur auf Knöpfen. Liegezeiten, „versandfertig" und der
 *    Rollenhinweis tragen ausschließlich Schriftfarbe.
 *  - Ungeöffnete Zugänge tragen Neon-Orange: ein Kreis mit Anzahl auf der
 *    Kachel, ein Punkt mit „NEU" in der Karte. Beides verschwindet beim
 *    Öffnen, ohne dass jemand etwas abhaken muss.
 *  - Der Arbeitsvorrat einer Kollegin lässt sich ansehen. Dann wechselt die
 *    ganze Seite den Grundton, damit niemand versehentlich fremd arbeitet.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import {
  IconGesendet,
  IconHaken,
  IconInfo,
  IconLkw,
  IconPaket,
  IconRechner,
  IconTelefon,
  IconUhr,
  IconWerkzeug,
} from '../komponenten/Icons';
import { Anrufuhr } from '../komponenten/Anrufuhr';
import { Terminbereich } from '../komponenten/Terminbereich';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraHinweise } from '../komponenten/KoraHinweise';
import { eur } from '../hilfen/format';
import type { KachelKey, MeinTagAntwort, MeinTagKachel, MeinTagZeile, Ton } from '../typen';
import '../komponenten/chat.css';
import './meintag.css';
import { Menueknopf } from '../komponenten/Shell';
import { MontageDialog } from '../modals/MontageDialog';

const ICONS: Record<KachelKey, (p: { groesse?: number }) => JSX.Element> = {
  kalkulieren: IconRechner,
  pruefen: IconHaken,
  versenden: IconGesendet,
  nachfassen: IconTelefon,
  beschaffung: IconPaket,
  lieferung: IconLkw,
  service: IconWerkzeug,
  wiedervorlage: IconUhr,
};

/** Was in einer leeren Rubrik steht – kein „0 Ergebnisse". */
const LEER: Record<KachelKey, string> = {
  kalkulieren: 'Nichts zu kalkulieren.',
  pruefen: 'Nichts zu prüfen.',
  versenden: 'Alles ist beim Kunden – kein Angebot wartet auf den Versand.',
  nachfassen: 'Gerade liegt nichts beim Kunden, das nachzufassen wäre.',
  beschaffung: 'Keine Bestellung wartet auf dich.',
  lieferung: 'Keine Lieferung, keine Montage offen.',
  service: 'Kein offener Servicepunkt.',
  wiedervorlage: 'Nichts ruht – kein Vorgang wartet auf ein Datum.',
};

/** Der Satz unter dem Rubrikkopf: was in dieser Rubrik zu tun ist. */
const ANLEITUNG: Record<KachelKey, string> = {
  kalkulieren: 'Rechnen, Summe eintragen, danach zur Prüfung geben.',
  pruefen: 'Gegen die Checkliste prüfen und für den Versand freigeben.',
  versenden: 'Kunden anrufen, Angebot ankündigen, danach versenden.',
  nachfassen: 'Anrufen und festhalten, was der Kunde sagt.',
  beschaffung: 'Aufmaß klären, bestellen, Auftragsbestätigung prüfen.',
  lieferung: 'Termin bestätigen, Lieferung verfolgen, Montage planen.',
  service: 'Servicepunkte terminieren und abschließen.',
  wiedervorlage: 'Kommt von selbst zurück – oder heute vorziehen.',
};

export function MeinTag() {
  const { nutzer, modalOeffnen } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* Voreinstellung beim Öffnen: „Versenden" (v6, Richtlinie 7). */
  const [rubrik, setRubrik] = useState<KachelKey>('versenden');
  const [alleUntereinander, setAlle] = useState(false);
  /** Wessen Vorrat gezeigt wird – leer heißt der eigene. */
  const [vertretung, setVertretung] = useState('');
  const [waehlerOffen, setWaehlerOffen] = useState(false);
  /** Offen heißt: „Montage planen" wurde geklickt (MONTAGEKALENDER.md 3.1). */
  const [montageFuer, setMontageFuer] = useState<number | null>(null);
  /*
   * Einklappen gibt es nicht mehr (v15, 8.1). Der Arbeitsvorrat ist das
   * Wichtigste der Seite; wird der Platz knapp, weicht das Hauptmenü in die
   * Schublade, nicht der Vorrat in einen 66-px-Streifen.
   */

  const abfrage = useQuery({
    queryKey: ['mein-tag', vertretung],
    queryFn: () => api.hole<MeinTagAntwort>(`/mein-tag${vertretung ? `?person=${encodeURIComponent(vertretung)}` : ''}`),
    staleTime: 30_000,
  });
  const d = abfrage.data;

  const gesehen = useMutation({
    mutationFn: (merker: string) => api.sende<{ ok: boolean }>('/mein-tag/gesehen', { merker }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mein-tag'] }),
  });

  /*
   * Vertretung färbt die ganze Anwendung, nicht nur diese Seite: Seitenleiste,
   * Inhaltsfläche und eine Kante über die volle Breite. Ein Balken allein
   * reicht nicht – wer sich vertut, arbeitet im Namen einer anderen Person.
   */
  const fremd = !!d && !d.eigen;
  useEffect(() => {
    if (fremd) document.body.dataset.vertretung = 'an';
    else delete document.body.dataset.vertretung;
    return () => {
      delete document.body.dataset.vertretung;
    };
  }, [fremd]);

  const kacheln = useMemo(() => d?.kacheln ?? [], [d]);
  const angebote = kacheln.filter((k) => k.gruppe === 'angebote');
  const auftraege = kacheln.filter((k) => k.gruppe === 'auftraege');

  /*
   * Beim Wechsel auf einen anderen Vorrat die erste gefüllte Rubrik zeigen.
   *
   * Sonst landet man auf der leeren Liste der eigenen Voreinstellung und hält
   * den fremden Tisch für leer. Der eigene Tag bleibt bei „Versenden", auch
   * wenn dort gerade nichts liegt – das ist die verabredete Startrubrik.
   */
  const gezeigterVorrat = useRef('');
  useEffect(() => {
    if (!d || d.fuer === gezeigterVorrat.current) return;
    const vorher = gezeigterVorrat.current;
    gezeigterVorrat.current = d.fuer;
    if (!vorher) return;
    const leer = (d.kacheln.find((k) => k.key === rubrik)?.anzahl ?? 0) === 0;
    const erste = d.kacheln.find((k) => k.anzahl > 0);
    if (leer && erste) setRubrik(erste.key);
  }, [d, rubrik]);

  /** Öffnen heißt gesehen: der Neu-Vermerk fällt im selben Zug weg. */
  function oeffnen(z: MeinTagZeile) {
    if (z.neu && d?.eigen) gesehen.mutate(z.merker);
    /*
     * „Montage planen" führt nicht in den Vorgang, sondern öffnet den
     * Montage-Dialog (MONTAGEKALENDER.md 3.1) – derselbe wie im Kalender
     * und im Auftrags-Detail. Den Termin trägt man in drei Feldern ein;
     * dafür erst eine Maske weiter zu springen wäre ein Umweg.
     */
    if (z.knopf === 'Montage planen') {
      setMontageFuer(z.angebotId);
      return;
    }
    navigate(`/angebot/${z.angebotId}`, {
      state: { herkunft: 'meintag', ...(z.oeffne ? { oeffne: z.oeffne } : {}) },
    });
  }

  const gezeigt: KachelKey[] = alleUntereinander ? kacheln.map((k) => k.key) : [rubrik];
  const tonVon = (key: KachelKey): Ton =>
    kacheln.find((k) => k.key === key)?.ton ?? {
      id: 'taupe',
      label: 'Taupe',
      akz: '#8B7B6E',
      akzTxt: '#6F6154',
      dunkel: '#6F6154',
      tint: '#F1EDE9',
    };

  return (
    <div className="mt">
      {/* --- Kopf ------------------------------------------------------- */}
      <div className="mt-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div className="mt-kopf-links">
          <h1 className="mt-titel">{d && !d.eigen ? `Tag von ${d.fuerName.split(' ')[0]}` : 'Mein Tag'}</h1>
          {d && (
            <span className="mt-kopf-satz">
              {langesDatum(d.heute)} · {d.gesamt} {d.gesamt === 1 ? 'Vorgang' : 'Vorgänge'}
              {d.eigen ? ' auf deinem Tisch' : ' auf diesem Tisch'}
              {d.ruhen > 0 ? ` · ${d.ruhen} ${d.ruhen === 1 ? 'ruht' : 'ruhen'}` : ''}
            </span>
          )}
        </div>

        {/*
          Das Nachrichten-Symbol steht links neben dem Arbeitsvorrat-Umschalter
          (TEAM-CHAT.md, 2.1). Es ist dasselbe Bauteil wie auf jeder anderen
          Maske, nur an seinem eigentlichen Platz.
        */}
        </div>
        <div className="mt-kopf-rechts">
          <Nachrichtenknopf />

        {d && (
          <div className="mt-waehler">
            <button
              type="button"
              className="mt-waehler-knopf"
              onClick={() => setWaehlerOffen((o) => !o)}
              title="Arbeitsvorrat einer anderen Person ansehen"
            >
              <span className="mt-waehler-avatar">
                {d.fuerName
                  .split(/\s+/)
                  .map((t) => t[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </span>
              <span className="mt-waehler-text">
                <span className="mt-waehler-kicker">Arbeitsvorrat</span>
                <span className="mt-waehler-name">{d.fuerName.split(' ')[0]}</span>
              </span>
              <span className="mt-waehler-pfeil">▾</span>
            </button>
            {waehlerOffen && (
              <>
                <div className="mt-waehler-schirm" onClick={() => setWaehlerOffen(false)} aria-hidden="true" />
                <div className="mt-waehler-liste" role="menu">
                  {d.team.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      className={`mt-waehler-zeile ${p.name === d.fuer ? 'aktiv' : ''}`}
                      onClick={() => {
                        setVertretung(p.name === nutzer?.name ? '' : p.name);
                        setWaehlerOffen(false);
                      }}
                    >
                      <span className="mt-avatar">{p.initialen}</span>
                      <span className="mt-waehler-person">{p.anzeigename}</span>
                      <span className="mt-waehler-zahl">{p.gesamt || '—'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {d && !d.eigen && (
        <div className="mt-vertretung">
          <span className="mt-vertretung-kicker">Vertretung</span>
          <span className="mt-vertretung-text">
            Du siehst den Arbeitsvorrat von {d.fuerName}. Die Knöpfe öffnen diese Vorgänge;
            quittiert wird weiterhin unter deinem Namen.
          </span>
          <button type="button" className="mt-vertretung-zurueck" onClick={() => setVertretung('')}>
            Zurück zu meinem Tag
          </button>
        </div>
      )}

      {abfrage.isLoading && <div className="eh-karte">Der Tag wird zusammengestellt …</div>}
      {abfrage.isError && <div className="eh-karte">Der Tag konnte nicht geladen werden.</div>}

      {d && (
        <div className="mt-inhalt">
          {/* --- Kachelspalte links (v15, 8) ---------------------------- */}
          <aside className={`mt-vorrat ${alleUntereinander ? '' : 'hat-wahl'}`}>
            <div className="mt-vorrat-kopf">
              <span className="mt-vorrat-kicker">Arbeitsvorrat</span>
            </div>

            <Band
              titel="Angebote"
              marke="var(--eh-brand)"
              kacheln={angebote}
              gewaehlt={alleUntereinander ? null : rubrik}
              onWahl={(k) => {
                setRubrik(k);
                setAlle(false);
              }}
            />

            <Band
              titel="Aufträge"
              marke="var(--eh-brand-dunkel)"
              kacheln={auftraege}
              gewaehlt={alleUntereinander ? null : rubrik}
              /* Ruhendes zählt nicht in die Bandsumme (8.2). */
              ohneRuhende
              onWahl={(k) => {
                setRubrik(k);
                setAlle(false);
              }}
            />
          </aside>

          {/* --- Die Liste der geöffneten Rubrik (v13, 9) --------------- */}
          <div className="mt-mitte">
            <div className="mt-listenkopf">
              <span className="mt-kicker">
                {alleUntereinander
                  ? 'Alle Rubriken untereinander'
                  : 'Deine Liste · links die Rubrik wechseln'}
              </span>
              <button
                type="button"
                className={`mt-textlink ${alleUntereinander ? 'aktiv' : ''}`}
                onClick={() => setAlle((v) => !v)}
              >
                {alleUntereinander ? 'Nur eine Rubrik' : 'Alles untereinander'}
              </button>
            </div>

            {/* --- Die Liste: eine Karte je Vorgang ------------------------ */}
            {gezeigt.map((key) => {
              const k = kacheln.find((x) => x.key === key)!;
              const zeilen = d.zeilen.filter((z) => z.kachel === key);
              const rolle = zeilen[0]?.liegtBei.rolleLabel ?? '';
              return (
                <section className="mt-liste" key={key}>
                  <div className="mt-liste-kopf">
                    <span className="mt-liste-titel" style={{ color: k.ton.akzTxt }}>
                      {k.label}
                    </span>
                    <span className="mt-liste-zusatz">
                      {k.anzahl} {k.anzahl === 1 ? 'Vorgang' : 'Vorgänge'}
                      {rolle ? ` · liegt beim ${rolle}` : ''}
                    </span>
                    <span className="mt-liste-rechts">
                      {k.kritisch > 0 && (
                        <span className="mt-liste-dringend">{k.kritisch} kritisch</span>
                      )}
                      {k.kritisch === 0 && k.baldFaellig > 0 && (
                        <span className="mt-liste-faellig">{k.baldFaellig} bald fällig</span>
                      )}
                      {!k.ohneSumme && k.summe > 0 && (
                        <span className="mt-liste-summe">{k.summeText} netto</span>
                      )}
                    </span>
                  </div>
                  <div className="mt-liste-anleitung">{ANLEITUNG[key]}</div>

                  <div className="mt-karten">
                    {zeilen.map((z) => (
                      <Karte key={z.key} z={z} ton={tonVon(key)} onOeffnen={() => oeffnen(z)} />
                    ))}
                    {zeilen.length === 0 && <div className="mt-leer">{LEER[key]}</div>}
                  </div>

                  {/*
                    KORA steht unter der Liste und spricht nur über diese
                    Rubrik – so sucht niemand mehr, in welcher Kachel der
                    genannte Vorgang liegt.
                  */}
                  <KoraHinweise
                    rubrik={alleUntereinander ? 'alle' : key}
                    rubrikLabel={k.label}
                    onOeffnen={(id) => {
                      const zeile = d.zeilen.find((x) => x.angebotId === id);
                      if (zeile) oeffnen(zeile);
                      else navigate(`/angebot/${id}`, { state: { herkunft: 'meintag' } });
                    }}
                  />
                </section>
              );
            })}
          </div>

          {/* --- Rechte Spalte ------------------------------------------- */}
          <div className="mt-rechts">

            {/*
              Reihenfolge nach v11, 5: Uhr, Kalender, Outlook, Team. Oben
              steht, was die Minute betrifft, unten, wer sonst noch arbeitet.
            */}
            <Anrufuhr zeit={meta.anrufzeit} />

            <Terminbereich person={vertretung} heute={d.heute} />

            <section className="mt-karte">
              <div className="mt-kicker-zeile">
                <span className="mt-kicker">Team · offene Vorgänge</span>
                <span className="mt-kicker-leise">Klick öffnet</span>
              </div>
              <div className="mt-team">
                {d.team.map((p) => {
                  const max = Math.max(1, ...d.team.map((t) => t.gesamt));
                  return (
                    <button
                      type="button"
                      className={`mt-person ${p.name === d.fuer ? 'eigen' : ''}`}
                      key={p.name}
                      title={`Arbeitsvorrat von ${p.anzeigename} ansehen`}
                      onClick={() => setVertretung(p.name === nutzer?.name ? '' : p.name)}
                    >
                      <span className="mt-avatar">{p.initialen}</span>
                      <span className="mt-person-text">
                        <span className="mt-person-kopf">
                          <span className="mt-person-name">{p.name}</span>
                          <span className="mt-person-menge">
                            {p.gesamt === 0
                              ? ''
                              : `${p.angebote} ${p.angebote === 1 ? 'Angebot' : 'Angebote'} · ${p.auftraege} ${p.auftraege === 1 ? 'Auftrag' : 'Aufträge'}`}
                          </span>
                        </span>
                        <span className="mt-balken" style={{ width: `${(p.gesamt / max) * 100}%` }}>
                          <span
                            className="mt-balken-angebote"
                            style={{ width: p.gesamt ? `${(p.angebote / p.gesamt) * 100}%` : '0%' }}
                          />
                        </span>
                      </span>
                      <span className={`mt-person-zahl ${p.gesamt === 0 ? 'leise' : ''}`}>
                        {p.gesamt === 0 ? 'nichts offen' : p.gesamt}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="mt-fuss">
              <span>
                Kora {meta.version}
                {meta.trockenlauf ? ' · Testbetrieb, Kundenmails werden nicht versendet' : ''}
              </span>
              <button
                type="button"
                className="mt-info"
                title="Was ist neu?"
                onClick={() => modalOeffnen({ art: 'changelog', alle: true })}
              >
                <IconInfo />
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        „Montage planen" öffnet denselben Dialog wie der Kalender und das
        Auftrags-Detail (MONTAGEKALENDER.md 3.1).
      */}
      {montageFuer !== null && (
        <MontageDialog
          einsatz={null}
          angebotId={montageFuer}
          onSchliessen={() => setMontageFuer(null)}
          onFertig={() => {
            setMontageFuer(null);
            void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
            void queryClient.invalidateQueries({ queryKey: ['montage'] });
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Ein Band der Kachelspalte: Kopf und vier Rubrikzeilen (v13, 8.2 und 8.3).
 *
 * Bis v12 war das ein breiter Kasten mit vier Kacheln und einem Summenfeld
 * daneben, quer über die Seite. Die Liste begann dadurch erst unterhalb des
 * Falzes: Wer eine Rubrik wechselte, verlor sie aus dem Blick und musste
 * wieder hinunterrollen. Jetzt steht das Band schmal links, die Liste
 * daneben, und beide sind gleichzeitig zu sehen.
 */
function Band({
  titel,
  marke,
  kacheln,
  gewaehlt,
  ohneRuhende = false,
  onWahl,
}: {
  titel: string;
  /** Die Bandmarke als CSS-Wert – Angebote Brandrot, Aufträge die dunkle Stufe. */
  marke: string;
  kacheln: MeinTagKachel[];
  /** Die gewählte Rubrik – null, wenn alles untereinander steht. */
  gewaehlt: KachelKey | null;
  /** Aufträge zählen die Wiedervorlage nicht mit – sie ruht. */
  ohneRuhende?: boolean;
  onWahl: (k: KachelKey) => void;
}) {
  /*
   * Bandkopf dreizeilig (v15, 8.2): Marke, Titel und Anzahl in der ersten
   * Zeile, darunter die Summe groß, darunter in einer Zeile, was in ihr
   * nicht steckt. Vorher stand die Summe klein und grau rechts neben dem
   * Titel – sie ist die Zahl, wegen der man hersieht, und stand wie eine
   * Fußnote da.
   */
  const gezaehlt = kacheln.filter((k) => !k.ohneSumme && (!ohneRuhende || !k.ruht));
  const summe = gezaehlt.reduce((s, k) => s + k.summe, 0);
  const anzahl = kacheln.reduce((s, k) => s + k.anzahl, 0);

  /* Was nicht in der Summe steckt, wird benannt – sonst fehlt sie unerklärt. */
  const ausgenommen = kacheln
    .filter((k) => k.ohneSumme || (ohneRuhende && k.ruht))
    .map((k) => (ohneRuhende && k.ruht ? `${k.label} ruht` : `${k.label} noch ohne Summe`));
  const note = ['netto', ...ausgenommen].join(' · ');

  return (
    <section className="mt-vorrat-reihe">
      <span className="mt-bandkopf">
        <span className="mt-bandzeile">
          <span className="mt-bandpunkt" style={{ background: marke }} />
          <span className="mt-bandtitel">{titel}</span>
          <span className="mt-bandzahl">
            {anzahl} {anzahl === 1 ? 'Vorgang' : 'Vorgänge'}
          </span>
        </span>
        <span className="mt-bandsumme">{eur(summe)}</span>
        <span className="mt-bandnote">{note}</span>
      </span>
      {kacheln.map((k) => (
        <Rubrikzeile
          key={k.key}
          k={k}
          aktiv={gewaehlt === k.key}
          onKlick={() => onWahl(k.key)}
        />
      ))}
    </section>
  );
}

/**
 * Eine Rubrik als Zeile (v13, 8.3).
 *
 * Aktiv heißt „hier bist du", nicht „hier drücken": weiche Aktionsfläche und
 * eine 3-px-Kante, dieselbe Aussage wie am aktiven Menüpunkt. Die gefüllte
 * Kachel in Prozessfarbe mit weißer Schrift war ein Knopf, der aussah, als
 * löste er etwas aus.
 */
function Rubrikzeile({
  k,
  aktiv,
  onKlick,
}: {
  k: MeinTagKachel;
  aktiv: boolean;
  onKlick: () => void;
}) {
  const Icon = ICONS[k.key];
  const leer = k.anzahl === 0;

  const titel =
    `${k.label} · ${k.anzahl} ${k.anzahl === 1 ? 'Vorgang' : 'Vorgänge'}` +
    (k.kritisch > 0 ? ` · ${k.kritisch} kritisch` : '') +
    (k.neu > 0 ? ` · ${k.neu} ungeöffnet` : '');

  return (
    <button
      type="button"
      role="tab"
      aria-selected={aktiv}
      className={`mt-rubrik ${aktiv ? 'aktiv' : ''} ${leer ? 'leer' : ''}`}
      title={titel}
      onClick={onKlick}
    >
      <span className="mt-rubrik-symbol" style={{ color: k.ton.akz }}>
        <Icon groesse={15} />
      </span>
      {/*
        Name und darunter die Summe der Rubrik (v15, 8.3). Leere und ruhende
        Rubriken bekommen keine Zeile: Bei ihnen wäre sie immer „0 €" oder
        „ohne Summe" – acht Zeilen, die nichts sagen.
      */}
      <span className="mt-rubrik-text">
        <span className="mt-rubrik-label">{k.label}</span>
        {!leer && !k.ruht && (
          <span className="mt-rubrik-summe">{k.ohneSumme ? 'ohne Summe' : eur(k.summe)}</span>
        )}
      </span>
      {/* Nur kritisch bekommt den Punkt – „bald fällig" steht im Rubrikkopf. */}
      {k.kritisch > 0 && (
        <span className="mt-rubrik-krit" title={`${k.kritisch} kritisch`} />
      )}
      {k.neu > 0 && (
        <span className="mt-rubrik-neu" title={`${k.neu} ungeöffnet`}>
          {k.neu}
        </span>
      )}
      <span className="mt-rubrik-zahl">{k.anzahl}</span>
    </button>
  );
}

/**
 * Eine Karte der Liste – ein Vorgang, eine Fläche.
 *
 * Die Kante links trägt die Farbe der Rubrik; das ist die ganze Verbindung
 * zwischen Kachel und Liste. Auszeichnungen wie Liegezeit oder
 * „versandfertig" tragen nur Schriftfarbe: Eine Fläche verspricht, dass man
 * klicken kann.
 */
function Karte({ z, ton, onOeffnen }: { z: MeinTagZeile; ton: Ton; onOeffnen: () => void }) {
  const ruhig = z.kachel === 'wiedervorlage';
  /* Der Chip trägt die Statusfarbe – Soft-Fläche mit dunklem Text (v11, 1.3). */
  const chip =
    z.plakette?.ton === 'rot' ? 'krit' : z.plakette?.ton === 'terrakotta' ? 'warn' : 'ok';

  return (
    <div className="mt-karte-zeile" style={{ borderLeftColor: ton.akz }}>
      <div className="mt-vorgang">
        <span className="mt-vorgang-kopf">
          {z.neu && (
            <span className="mt-neu" title="Neu auf deinem Tisch – noch nicht geöffnet">
              <span className="mt-neu-punkt" />
              NEU
            </span>
          )}
          <button type="button" className="mt-nummer" title={`${z.name} öffnen`} onClick={onOeffnen}>
            {z.name}
          </button>
          {/*
            Die Sprechblase (TEAM-CHAT.md, 3.2): Sie zeigt, wo diskutiert
            wird, ohne dass man jeden Vorgang öffnen muss. Keine Kommentare
            heißt keine Blase – eine Null an jeder Zeile wäre Rauschen.
          */}
          {z.kommentare.gesamt > 0 && (
            <button
              type="button"
              className={`mt-blase ${z.kommentare.ungelesen > 0 ? 'offen' : ''}`}
              title={
                `${z.kommentare.gesamt} ${z.kommentare.gesamt === 1 ? 'Kommentar' : 'Kommentare'} · ` +
                (z.kommentare.ungelesen > 0 ? `${z.kommentare.ungelesen} ungelesen` : 'alle gelesen')
              }
              onClick={onOeffnen}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.8-.9L3 21l1.9-5A8.4 8.4 0 1 1 21 11.5z" />
              </svg>
              {z.kommentare.gesamt}
            </button>
          )}
        </span>
        <span className="mt-unter">{z.unter}</span>
      </div>

      <div className="mt-rolle mt-weg">
        {z.liegtBei.uebergeben ? 'übergeben' : z.liegtBei.rolleLabel}
      </div>

      <div className="mt-stand mt-weg">
        <span className="mt-stand-oben">
          {z.plakette && <span className={`eh-status-chip ${chip}`}>{z.plakette.text}</span>}
          <span className="mt-stand-text" title={z.stand}>
            {z.stand}
          </span>
        </span>
        <span className="mt-stand-unter" title={z.standUnter}>
          {z.standUnter}
        </span>
      </div>

      <div className={`mt-summe ${z.summe === null ? 'leer' : ''}`}>{z.summeText}</div>

      <div className="mt-aktion">
        {ruhig ? (
          <button type="button" className="mt-textlink" title="Heute wieder auf den Tisch holen" onClick={onOeffnen}>
            vorziehen
          </button>
        ) : (
          <button
            type="button"
            className={`mt-knopf ${z.rot ? 'mt-knopf--rot' : ''}`}
            title={`${z.knopf} – öffnet den Vorgang an der richtigen Stelle`}
            onClick={onOeffnen}
          >
            {z.knopf}
          </button>
        )}
      </div>
    </div>
  );
}

/*
 * Der Ring „Auf meinem Tisch" ist mit v11 entfallen: Er zeigte dieselben
 * Zahlen wie die beiden Bandsummen, nur runder. Die Kennzahl der Seite steht
 * jetzt neben den Kacheln.
 */

/**
 * Datum ausgeschrieben, mit Wochentag: „Donnerstag, 17. September 2026".
 * Gerechnet aus dem Serverdatum, nicht aus der Systemuhr des Arbeitsplatzes.
 */
function langesDatum(iso: string): string {
  const tage = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const monate = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];
  const d = new Date(iso + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return `${tage[d.getDay()]}, ${d.getDate()}. ${monate[d.getMonth()]} ${d.getFullYear()}`;
}
